import {
  funcaoParticipanteDescriptions,
  tipoAcessoParticipanteHistoricoDescriptions,
  funcaoParticipanteHistoricoDescriptions,
} from 'common/genericDescriptions';
import {
  CadastroParticipanteRequest,
  ParticipanteResponse,
  TipoAcessoParticipanteHistorico,
  FuncaoParticipanteHistorico,
  DocumentParticipanteHistorico,
  RepresentanteLegalHistorico,
  UsuarioParticipante,
  Perfil,
  TipoCargo
} from 'common/genericTypes';
import { Participante, RepresentanteLegalState } from 'store/modules/participantes/registro';
import moment from 'moment';
import { addCpfCnpjMask, addTelefoneMask, addCEPMask } from 'utils/mask';
import { stripCpfCnpjFormatting } from 'utils/validation';

export default class ParticipanteParser {
  static stateToCadastroParticipanteApiRequest(participante: Participante): CadastroParticipanteRequest {
    const representantesLegais: Array<RepresentanteLegalState> = participante.representantesLegais.map((r) => {
      return { ...r, cpf: r.cpf.replace(/\D/g, '') };
    });

    const usuarios = [
      ...participante.representantesLegais.map((participante) => ({
        nome: participante.nome,
        email: participante.email,
        perfil: Perfil.Master,
        tipo: TipoCargo.RepresentanteLegal,
        cpf: participante.cpf?.replace(/\D/g, ''),
      } as UsuarioParticipante)),
      ...participante.usuarios.map((usuario) => ({
        ...usuario,
        cpf: usuario.cpf?.replace(/\D/g, ''),
      })), 
    ];

    return {
      tipoAcesso: participante.tipoAcesso,
      funcoes: participante.funcoes.map((funcao) => {
        return { funcao: funcao.value };
      }),
      cnpj: stripCpfCnpjFormatting(participante.cnpjParticipante),
      razaoSocial: participante.razaoSocial,
      cep: participante.endereco.cep.replace(/\D/g, ''),
      logradouro: participante.endereco.logradouro,
      numero: participante.endereco.numero,
      complemento: participante.endereco.complemento,
      bairro: participante.endereco.bairro,
      cidade: participante.endereco.cidade,
      uf: participante.endereco.uf,
      telefones: participante.contato.telefones.map((tel) => tel.replace(/\D/g, '')),
      atividadePrincipalDesenvolvida: participante.atividadePrincipal,
      faturamentoMedioMensalUltimosDozeMeses: participante.faturamento as number,
      razaoSocialBancoLiquidante: participante.razaoSocialBancoLiquidante,
      ispbBancoLiquidante: participante.ispbBancoLiquidante.replace(/\D/g, ''),
      agenciaBancoLiquidante: participante.agenciaBancoLiquidante,
      contaBancoLiquidante: participante.contaBancoLiquidante,
      inscricaoEstadual: participante.inscricaoEstadual,
      inscricaoMunicipal: participante.inscricaoMunicipal,
      grupoEconomico: participante.grupoEconomico,
      representantesLegais,
      usuarios
    };
  }

  static apiResponseToParticipanteState(response: ParticipanteResponse): Participante {
    return {
      tipoAcesso: response.tipoAcesso,
      funcoes: response.funcoes.map((_) => {
        return { description: funcaoParticipanteDescriptions[_.funcao], value: _.funcao };
      }),
      razaoSocial: response.razaoSocial,
      cnpjParticipante: response.cnpj,
      endereco: {
        cep: response.cep,
        logradouro: response.logradouro,
        numero: response.numero,
        complemento: response.complemento,
        bairro: response.bairro,
        cidade: response.cidade,
        uf: response.uf,
      },
      contato: {
        telefones: response.telefones,
      },
      atividadePrincipal: response.atividadePrincipalDesenvolvida,
      faturamento: response.faturamentoMedioMensalUltimosDozeMeses ?? null,
      razaoSocialBancoLiquidante: response.razaoSocialBancoLiquidante,
      ispbBancoLiquidante: response.ispbBancoLiquidante,
      agenciaBancoLiquidante: response.agenciaBancoLiquidante,
      contaBancoLiquidante: response.contaBancoLiquidante,
      documentos: response.documentos.map(({ participanteDocumentType, ...remoteFileInfo }) => ({
        type: participanteDocumentType,
        remoteFileInfo,
      })),
      representantesLegais: response.representantesLegais,
      documentosExcluir: [],
      usuarios: response.usuarios,
      inscricaoEstadual: response.inscricaoEstadual ?? '',
      inscricaoMunicipal: response.inscricaoMunicipal ?? '',
      grupoEconomico: response.grupoEconomico ?? '',
    };
  }

  static formatarCampoAlteracoes(nomeDoCampo: string, campo: any) {
    switch (nomeDoCampo) {
      case 'Suspenso':
        return JSON.parse(campo) === true ? 'VERDADEIRO' : 'FALSO';
      case 'DataUltimaAlteracao':
      case 'DataCriacao':
        return moment(JSON.parse(campo)).format('DD/MM/YYYY');
      case 'TipoAcesso':
        return tipoAcessoParticipanteHistoricoDescriptions[JSON.parse(campo) as TipoAcessoParticipanteHistorico];
      case 'Funcoes':
        const fParsed = JSON.parse(campo) as Array<{ Funcao: FuncaoParticipanteHistorico }>;
        return fParsed.map((f) => funcaoParticipanteHistoricoDescriptions[f.Funcao]).join(', ');
      case 'Cep':
        return addCEPMask(JSON.parse(campo));
      case 'CnpjBancoLiquidante':
      case 'Cnpj':
        return addCpfCnpjMask(JSON.parse(campo));
      case 'Telefones':
        const telefones = JSON.parse(campo) as Array<{ Telefone: string }>;
        return telefones.map((tel) => addTelefoneMask(tel.Telefone)).join(', ');
      case 'RepresentantesLegais':
        const rlParsed = JSON.parse(campo);
        if (!rlParsed || rlParsed.length === 0) return [];
        const rl = rlParsed as Array<RepresentanteLegalHistorico>;
        return rl;
      case 'Documentos':
        const parsed = JSON.parse(campo);
        if (!parsed || parsed.length === 0) return [];
        const documento = parsed as Array<DocumentParticipanteHistorico>;
        return documento;
      default:
        return JSON.parse(campo) as string;
    }
  }
}
